/* ==========================================================================
   NAS ME Pro - media loading

   Runs over every image and video inside a nas-mepro-* section and gives each
   one a state, so a slot that is still loading looks deliberate instead of
   empty. Pairs with nas-mepro-media.css.

   What it does:
     - Marks each media element pending -> ready, or failed after one retry.
     - Retries once with a cache-buster, which clears up the single dropped
       request that a flaky connection usually causes.
     - Gives every <video> a preload hint if the section did not set one.
     - Warms the media that is still queued once the page has gone idle, a few
       at a time. This is the part that makes the carousel feel instant: its
       off-screen slides are already in cache by the time you press the arrow,
       without any of it competing with the first screen.

   Everything is inside the closure, and it is safe to load twice - elements
   are tagged once claimed.
   ========================================================================== */
(function () {
    'use strict';

    var SECTION_SELECTOR = '[id^="nas-mepro-"]';
    var CLAIMED = 'data-nas-media';

    var PENDING = 'nas-media--pending';
    var READY = 'nas-media--ready';
    var FAILED = 'nas-media--failed';

    // How many warm-up downloads may be in flight at once
    var WARM_CONCURRENCY = 3;

    function each(list, fn) {
        Array.prototype.forEach.call(list, fn);
    }

    function settle(el, klass) {
        el.classList.remove(PENDING);
        el.classList.add(klass);
    }

    /* ---- Images -------------------------------------------------------- */

    function alreadyPainted(img) {
        // naturalWidth stays 0 for a broken file even once complete is true
        return img.complete && img.naturalWidth > 0;
    }

    function retryOnce(img) {
        if (img.getAttribute('data-nas-media-retried') === 'true') {
            settle(img, FAILED);
            return;
        }

        img.setAttribute('data-nas-media-retried', 'true');

        var src = img.currentSrc || img.src;

        if (!src) {
            settle(img, FAILED);
            return;
        }

        /* A fresh query string sidesteps the failed entry the browser may have
           cached, and srcset has to go or it would just pick the same URL. */
        img.removeAttribute('srcset');
        img.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'nas-retry=1';
    }

    function watchImage(img) {
        if (alreadyPainted(img)) {
            img.classList.add(READY);
            return;
        }

        img.classList.add(PENDING);

        img.addEventListener('load', function () {
            // A retry that also failed still fires load in some engines
            if (alreadyPainted(img)) {
                settle(img, READY);
            } else {
                retryOnce(img);
            }
        });

        img.addEventListener('error', function () {
            retryOnce(img);
        });
    }

    /* ---- Videos -------------------------------------------------------- */

    function watchVideo(video) {
        /* metadata is enough to show the first frame and size the box, and it
           keeps a handful of autoplaying clips from saturating the line. */
        if (!video.getAttribute('preload')) {
            video.preload = 'metadata';
        }

        // readyState 1 is HAVE_METADATA - dimensions and first frame are known
        if (video.readyState >= 1) {
            video.classList.add(READY);
            return;
        }

        video.classList.add(PENDING);

        ['loadeddata', 'canplay'].forEach(function (name) {
            video.addEventListener(name, function () {
                settle(video, READY);
            });
        });

        video.addEventListener('error', function () {
            /* A poster is still worth showing, so this counts as ready when
               there is one and only fails outright when there is not. */
            settle(video, video.getAttribute('poster') ? READY : FAILED);
        });
    }

    /* ---- Warm the queue once the first screen is done ------------------ */

    /* Collects the images the browser has not fetched yet - the lazy ones
       further down and inside the carousel - and pulls them through a short
       queue. Nothing is inserted into the page; this only fills the HTTP
       cache, so the <img> that eventually needs it resolves instantly. */
    function warm(images) {
        var queue = images.filter(function (img) {
            /* Only the lazy ones. An eager image is already on the wire, so
               warming it would just duplicate a request in flight; the ones
               worth pulling forward are exactly those the browser is holding
               back - the carousel's off-screen slides and everything below
               the fold. */
            return img.getAttribute('loading') === 'lazy'
                && !alreadyPainted(img)
                && (img.currentSrc || img.src);
        });

        if (!queue.length) {
            return;
        }

        var cursor = 0;

        function next() {
            if (cursor >= queue.length) {
                return;
            }

            var img = queue[cursor++];
            var probe = new Image();

            probe.onload = next;
            probe.onerror = next;

            if (img.srcset) {
                probe.srcset = img.srcset;
                if (img.sizes) {
                    probe.sizes = img.sizes;
                }
            }

            probe.src = img.currentSrc || img.src;
        }

        for (var i = 0; i < WARM_CONCURRENCY; i++) {
            next();
        }
    }

    /* Videos with preload="none" never even fetch a first frame, so a tab or
       carousel slide that has not been shown yet is still a black rectangle
       when it appears. Pull metadata (and the poster) in the same idle window
       as the images, a few at a time. */
    function warmVideos(videos) {
        var queue = videos.filter(function (video) {
            return video.readyState < 1;
        });

        if (!queue.length) {
            return;
        }

        var cursor = 0;

        function next() {
            if (cursor >= queue.length) {
                return;
            }

            var video = queue[cursor++];
            var settled = false;

            function done() {
                if (settled) {
                    return;
                }

                settled = true;
                video.removeEventListener('loadeddata', done);
                video.removeEventListener('error', done);
                next();
            }

            if (video.getAttribute('preload') === 'none') {
                video.preload = 'metadata';
            }

            video.addEventListener('loadeddata', done);
            video.addEventListener('error', done);

            try {
                video.load();
            } catch (err) {
                done();
                return;
            }

            window.setTimeout(done, 8000);
        }

        for (var i = 0; i < Math.min(2, queue.length); i++) {
            next();
        }
    }

    function whenIdle(fn) {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(fn, { timeout: 3000 });
            return;
        }

        window.setTimeout(fn, 1200);
    }

    /* ---- Wiring -------------------------------------------------------- */

    function scan(scope) {
        var sections = (scope || document).querySelectorAll(SECTION_SELECTOR);

        if (!sections.length) {
            return;
        }

        var images = [];
        var videos = [];

        each(sections, function (section) {
            each(section.querySelectorAll('img, video'), function (el) {
                if (el.hasAttribute(CLAIMED)) {
                    return;
                }

                el.setAttribute(CLAIMED, '');

                if (el.tagName === 'IMG') {
                    images.push(el);
                    watchImage(el);
                } else {
                    videos.push(el);
                    watchVideo(el);
                }
            });
        });

        if (images.length || videos.length) {
            // Only after the page has settled, so first paint keeps the line
            whenIdle(function () {
                if (images.length) {
                    warm(images);
                }

                if (videos.length) {
                    warmVideos(videos);
                }
            });
        }
    }

    function boot() {
        scan();
    }

    /* This file may be loaded in <head>, so wait for a body if there is none
       yet; when it is loaded at the end of a section, the DOM is already up. */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }

    // Re-scan whatever the theme editor swapped in
    document.addEventListener('shopify:section:load', function (event) {
        scan(event.target);
    });
})();
